import { StateCreator } from "zustand";

import {
  ArticleRegister,
  Entry,
  FiscalYear,
  InventoryItem,
  statesEnglishToSwedish
} from "models/inventory/inventory";
import { RootState } from "store";
import { InventoryAPI } from "api/inventory";

export interface InventorySlice {
  articleRegister?: ArticleRegister;
  fiscalYear: FiscalYear;
  fiscalYearNotFound: boolean;
  isCreatingInventory: boolean;
  isCreatingInventoryPdf: boolean;
  isLoadingArticleRegister: boolean;
  isLoadingFiscalYear: boolean;
  isLoadingInventories: boolean;
  isLoadingInventory: boolean;
  isSavingInventory: boolean;
  isUpdatingInventory: boolean;
  isUpdatingInventoryState: boolean;
  inventories: InventoryItem[];
  lastYearsInventory?: InventoryItem;
  inventory?: InventoryItem;
  statesEnglishToSwedish: typeof statesEnglishToSwedish;

  clearInventory: () => void;
  createdInventory: () => void;
  createdInventoryPdf: () => void;
  createInventoryFailed: () => void;
  createInventoryPdfFailed: () => void;
  creatingInventory: () => void;
  creatingInventoryPdf: () => void;
  setFiscalYearNotFound: () => void;
  loadingArticleRegister: () => void;
  loadedArticleRegister: (articleRegister: ArticleRegister) => void;
  loadArticleRegisterFailed: () => void;
  loadingInventories: () => void;
  loadInventoriesFailed: () => void;
  loadingInventory: () => void;
  loadedInventories: (inventories: InventoryItem[]) => void;
  loadedInventory: (inventory: InventoryItem) => void;
  loadInventoryFailed: () => void;
  loadingFiscalYear: () => void;
  loadedFiscalYear: (fiscalYear: FiscalYear) => void;
  loadFiscalYearFailed: () => void;
  updatedInventory: (inventory: Partial<InventoryItem>) => void;
  updatedInventoryState: () => void;
  updateInventoryFailed: () => void;
  updateInventoryStateFailed: () => void;
  updatingInventory: () => void;
  updatingInventoryState: () => void;
  savedInventory: () => void;
  savingInventory: () => void;
  getArticleRegister: () => void;
  getFiscalYear: (customerId: string) => void;
  getInventories: (customerId: string) => void;
  getInventory: (customerId: string, inventoryId: number) => void;
  createInventory: (customerId: string, perDate: string) => void;
  createInventoryPdf: (
    customerId: string,
    inventoryId: number,
    companyName: string,
    username: string
  ) => void;
  updateInventory: (
    customerId: string,
    inventoryId: number,
    inventoryEntries: Entry[],
    state: string
  ) => void;
  updateInventoryState: (
    customerId: string,
    inventoryId: number,
    state: string
  ) => void;
}

const initialInventoryState = {
  articleRegister: undefined,
  fiscalYear: {
    fiscalYearMonthStart: "",
    fiscalYearDayStart: "",
    fiscalYearDayEnd: "",
    fiscalYearMonthEnd: ""
  },
  fiscalYearNotFound: false,
  isCreatingInventory: false,
  isCreatingInventoryPdf: false,
  isLoadingArticleRegister: false,
  isLoadingFiscalYear: true,
  isLoadingInventories: false,
  isLoadingInventory: false,
  isSavingInventory: false,
  isUpdatingInventory: false,
  isUpdatingInventoryState: false,
  inventories: [],
  inventory: undefined,
  lastYearsInventory: undefined,
  statesEnglishToSwedish: {
    created: "Skapad",
    ongoing: "Pågående",
    completed: "Slutförd"
  }
};

export const createInventorySlice: StateCreator<
  RootState,
  [["zustand/devtools", never]],
  [],
  InventorySlice
> = (set, get) => ({
  ...initialInventoryState,

  clearInventory: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          inventory: undefined
        }
      }),
      false,
      "inventory/CLEAR_INVENTORY"
    ),

  createdInventory: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isCreatingInventory: false
        }
      }),
      false,
      "inventory/CREATED_INVENTORY"
    ),

  createdInventoryPdf: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isCreatingInventoryPdf: false
        }
      }),
      false,
      "inventory/CREATED_INVENTORY_PDF"
    ),

  createInventoryFailed: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isCreatingInventory: false
        }
      }),
      false,
      "inventory/CREATE_INVENTORY_FAILED"
    ),

  createInventoryPdfFailed: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isCreatingInventoryPdf: false
        }
      }),
      false,
      "inventory/CREATE_INVENTORY_PDF_FAILED"
    ),

  creatingInventory: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isCreatingInventory: true
        }
      }),
      false,
      "inventory/CREATING_INVENTORY"
    ),

  creatingInventoryPdf: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isCreatingInventoryPdf: true
        }
      }),
      false,
      "inventory/CREATING_INVENTORY_PDF"
    ),

  setFiscalYearNotFound: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          fiscalYearNotFound: true
        }
      }),
      false,
      "inventory/SET_FISCAL_YEAR_NOT_FOUND"
    ),

  loadingArticleRegister: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isLoadingArticleRegister: true
        }
      }),
      false,
      "inventory/LOADING_ARTICLE_REGISTER"
    ),

  loadedArticleRegister: (articleRegister: ArticleRegister) =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isLoadingArticleRegister: false,
          articleRegister
        }
      }),
      false,
      "inventory/LOADED_ARTICLE_REGISTER"
    ),

  loadArticleRegisterFailed: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isLoadingArticleRegister: false
        }
      }),
      false,
      "inventory/LOAD_ARTICLE_REGISTER_FAILED"
    ),

  loadingInventories: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isLoadingInventories: true
        }
      }),
      false,
      "inventory/LOADING_INVENTORIES"
    ),

  loadInventoriesFailed: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isLoadingInventories: false
        }
      }),
      false,
      "inventory/LOADING_INVENTORIES_FAILED"
    ),

  loadingInventory: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isLoadingInventory: true
        }
      }),
      false,
      "inventory/LOADING_INVENTORY"
    ),

  loadedInventories: (inventories) => {
    if (inventories && inventories.length > 0) {
      const completed = inventories.filter(
        (inv) => inv.state_name === "completed"
      );
      if (completed && completed.length > 0) {
        // Sort by descending complete date
        completed.sort((fst, snd) => {
          return fst.per_date < snd.per_date ? 1 : -1;
        });
        set(
          ({ inventory }) => ({
            inventory: {
              ...inventory,
              lastYearsInventory: completed[0]
            }
          }),
          false,
          "inventory/SET_LAST_YEARS_INVENTORY"
        );
      }
    }

    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          inventories,
          isLoadingInventories: false
        }
      }),
      false,
      "inventory/LOADED_INVENTORIES"
    );
  },

  loadedInventory: (loadedInventory) =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          inventory: loadedInventory,
          isLoadingInventory: false
        }
      }),
      false,
      "inventory/LOADED_INVENTORY"
    ),

  loadInventoryFailed: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isLoadingInventory: false
        }
      }),
      false,
      "inventory/LOAD_INVENTORY_FAILED"
    ),

  loadingFiscalYear: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          fiscalYearNotFound: false,
          isLoadingFiscalYear: true
        }
      }),
      false,
      "inventory/LOADING_FISCAL_YEAR"
    ),

  loadedFiscalYear: (fiscalYear) =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          fiscalYear,
          isLoadingFiscalYear: false
        }
      }),
      false,
      "inventory/LOADED_FISCAL_YEAR"
    ),

  loadFiscalYearFailed: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isLoadingFiscalYear: false
        }
      }),
      false,
      "inventory/LOAD_FISCAL_YEAR_FAILED"
    ),

  updatedInventory: (update) => {
    const { inventory: existingInventory } = get().inventory;
    let updatedInventory: InventoryItem;
    if (existingInventory) {
      updatedInventory = { ...existingInventory, ...update };
    }
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isUpdatingInventory: false,
          inventory: updatedInventory
        }
      }),
      false,
      "inventory/UPDATED_INVENTORY"
    );
  },

  updatedInventoryState: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isUpdatingInventoryState: false
        }
      }),
      false,
      "inventory/UPDATED_INVENTORY_STATE"
    ),

  updateInventoryFailed: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isUpdatingInventory: false
        }
      }),
      false,
      "inventory/UPDATE_INVENTORY_FAILED"
    ),

  updateInventoryStateFailed: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isUpdatingInventoryState: false
        }
      }),
      false,
      "inventory/UPDATE_INVENTORY_STATE_FAILED"
    ),

  updatingInventory: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isUpdatingInventory: true
        }
      }),
      false,
      "inventory/UPDATING_INVENTORY"
    ),

  updatingInventoryState: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isUpdatingInventoryState: true
        }
      }),
      false,
      "inventory/UPDATING_INVENTORY_STATE"
    ),

  savedInventory: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isSavingInventory: false
        }
      }),
      false,
      "inventory/SAVED_INVENTORY"
    ),

  savingInventory: () =>
    set(
      ({ inventory }) => ({
        inventory: {
          ...inventory,
          isSavingInventory: true
        }
      }),
      false,
      "inventory/SAVING_INVENTORY"
    ),

  getArticleRegister: async () => {
    set({}, false, "inventory/GET_ARTICLE_REGISTER");
    const { inventory, error } = get();

    inventory.loadingArticleRegister();

    try {
      const response = await InventoryAPI.getArticleRegister();
      inventory.loadedArticleRegister(response);
    } catch (err) {
      inventory.loadArticleRegisterFailed();
      error.appendErrorNotification("Öppning av artikelregistret misslyckades.", err);
    }
  },

  getFiscalYear: async (customerId) => {
    set({}, false, "inventory/GET_FISCAL_YEAR");
    const { inventory, error } = get();
    inventory.loadingFiscalYear();
    try {
      const response = await InventoryAPI.getFiscalYear(customerId);
      inventory.loadedFiscalYear(response);
    } catch (err) {
      inventory.loadFiscalYearFailed();
      let statusCode: number | undefined;

      if (error && typeof error === "object" && "status" in error) {
        statusCode = (error as { status: number }).status;
      }
      if (statusCode === 404) {
        inventory.setFiscalYearNotFound();
      }

      error.appendErrorNotification("Hämtning av räkenskapsår misslyckades.", err);
    }
  },

  getInventories: async (customerId) => {
    set({}, false, "inventory/GET_INVENTORIES");
    const { inventory, error } = get();
    inventory.loadingInventories();
    try {
      const response = await InventoryAPI.getInventories(customerId);
      inventory.loadedInventories(response ?? []);
    } catch (err) {
      inventory.loadInventoriesFailed();
      error.appendErrorNotification("Hämtning av inventeringar misslyckades.", err);
    }
  },

  getInventory: async (customerId, inventoryId) => {
    set({}, false, "inventory/GET_INVENTORY");
    const { inventory, error } = get();
    inventory.loadingInventory();
    try {
      const response = await InventoryAPI.getInventory(customerId, inventoryId);
      inventory.loadedInventory(response);
    } catch (err) {
      inventory.loadInventoryFailed();
      error.appendErrorNotification("Hämtning av inventering misslyckades.", err);
    }
  },

  createInventory: async (customerId, perDate) => {
    set({}, false, "inventory/CREATE_INVENTORY");
    const { inventory, error } = get();
    inventory.creatingInventory();
    try {
      const response = await InventoryAPI.createInventory(customerId, perDate);

      if (!response.ok) {
        throw new Error("Skapande av inventering misslyckades.");
      }

      inventory.createdInventory();
      inventory.getInventories(customerId);
    } catch (err) {
      inventory.createInventoryFailed();
      error.appendErrorNotification("Skapande av inventering misslyckades.", err);
    }
  },

  createInventoryPdf: async (
    customerId,
    inventoryId,
    companyName,
    username
  ) => {
    set({}, false, "inventory/CREATE_INVENTORY_PDF");
    const { inventory, error } = get();
    inventory.creatingInventoryPdf();
    try {
      const response = await InventoryAPI.createInventoryPdf(
        customerId,
        inventoryId,
        companyName,
        username
      );
      window.open(response.location, "_blank");
      inventory.createdInventoryPdf();
    } catch (err) {
      inventory.createInventoryPdfFailed();
      error.appendErrorNotification("Generering av PDF för inventeringen misslyckades.", err);
    }
  },

  updateInventory: async (customerId, inventoryId, inventoryEntries, state) => {
    set({}, false, "inventory/UPDATE_INVENTORY");
    const { inventory, error } = get();
    inventory.updatingInventory();
    try {
      await InventoryAPI.updateInventory(
        customerId,
        inventoryId,
        inventoryEntries,
        state
      );
      inventory.updatedInventory({
        id: inventoryId,
        customer_id: customerId,
        state_name: state,
        entries: inventoryEntries
      });
      inventory.getInventory(customerId, inventoryId);
      inventory.getInventories(customerId);
    } catch (err) {
      inventory.updateInventoryFailed();
      error.appendErrorNotification("Uppdatering av inventeringen misslyckades.", err);
    }
  },

  updateInventoryState: async (customerId, inventoryId, state) => {
    set({}, false, "inventory/UPDATE_INVENTORY_STATE");
    const { inventory, error } = get();
    inventory.updatingInventoryState();
    try {
      await InventoryAPI.updateInventoryState(customerId, inventoryId, state);
      inventory.updatedInventoryState();
      inventory.getInventories(customerId);
    } catch (err) {
      inventory.updateInventoryStateFailed();
      error.appendErrorNotification("Uppdatering av inventeringens status misslyckades.", err);
    }
  }
});
